import React, { useEffect, useState } from "react";
import { IndianRupee, ShoppingBag, Users, Store, Clock, AlertTriangle, CheckCircle2, ChefHat, Package, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { compact, rupee } from "../../lib/format";
import { StatCard, Panel, EntitySelect } from "./ui";
import { Header } from "./CorporateDashboard";
import { HourlyChart } from "./MallDashboard";

const LIVE = [
  { key: "NEW", label: "New", icon: ShoppingBag, color: "text-blue-400" },
  { key: "ACCEPTED", label: "Accepted", icon: CheckCircle2, color: "text-cyan-400" },
  { key: "PREPARING", label: "Preparing", icon: ChefHat, color: "text-amber-400" },
  { key: "READY", label: "Ready", icon: Package, color: "text-emerald-400" },
  { key: "DELAYED", label: "Delayed", icon: AlertTriangle, color: "text-red-400" },
];

export default function FoodCourtCommand({ ctx, user }) {
  const fcs = ctx.foodcourts;
  const [fcId, setFcId] = useState(user.foodcourt_id || fcs[0]?.id);
  const [data, setData] = useState(null);

  const fetchData = () => api.get(`/dashboard/foodcourt/${fcId}`).then((r) => setData(r.data));
  useEffect(() => {
    if (!fcId) return;
    fetchData();
    const t = setInterval(fetchData, 6000);
    return () => clearInterval(t);
  }, [fcId]);

  const selector = (
    <EntitySelect label="Food Court" value={fcId} onChange={setFcId} testid="command-fc-select"
      options={fcs.map((f) => ({ value: f.id, label: `${f.mall_name} · ${f.name}` }))} />
  );

  if (!data) return <div className="space-y-6"><Header title="Food Court Command" right={selector} /><Loader2 className="animate-spin text-amber-400" /></div>;
  const s = data.summary;

  return (
    <div className="space-y-6">
      <Header title="Live Command Centre" sub={`${data.foodcourt?.name} · auto-refreshing every 6s`} right={selector} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {LIVE.map((l) => (
          <div key={l.key} className="card-surface rounded-2xl p-4 text-center" data-testid={`live-${l.key}`}>
            <l.icon size={20} className={`mx-auto ${l.color}`} />
            <div className="font-display font-extrabold text-2xl mt-2">{data.live[l.key] || 0}</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide">{l.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Today Sales" value={compact(s.sales)} testid="stat-cmd-sales" />
        <StatCard icon={ShoppingBag} label="Orders" value={s.orders} sub={`AOV ${rupee(s.aov)}`} accent="blue" />
        <StatCard icon={Users} label="Customers" value={s.customers} accent="emerald" />
        <StatCard icon={Store} label="Restaurants / Order" value={s.restaurants_per_order} sub={`${s.items_per_order} items/order`} accent="gold" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Hourly Sales"><HourlyChart data={data.hourly} /></Panel>
        <Panel title="Zone Utilisation">
          <div className="space-y-3">
            {data.zones.map((z) => {
              const max = Math.max(...data.zones.map((x) => x.sales), 1);
              return (
                <div key={z.zone}>
                  <div className="flex justify-between text-sm mb-1"><span>Zone {z.zone}</span><span className="text-slate-400">{rupee(z.sales)} · {z.orders} orders</span></div>
                  <div className="h-2 rounded-full bg-[#16336B] overflow-hidden">
                    <div className="h-full gold-gradient rounded-full" style={{ width: `${(z.sales / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
